/* iter55g14 — Script Read-Through player.
 *
 * A lightweight in-dialog "cast player" that assigns a distinct
 * SpeechSynthesis voice + colour to every named character in an
 * `active` script and reads the fountain line-by-line so writers can
 * hear whether dialogue lands or clunks.
 *
 * Design constraints:
 *   • No backend, no LLM cost — uses the browser's built-in Web Speech
 *     API. If the browser has no voices, we still show the transcript
 *     player and colour cues (silent walk-through).
 *   • Parses the fountain on-mount into an ordered event stream
 *     [{type, character?, text}] and steps through it, so it stays in
 *     sync with edits made outside the player.
 *   • Skips title-page metadata and parentheticals (we still show them
 *     onscreen, we just don't speak them).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Play, Pause, Square, SkipBack, SkipForward, Volume2, Users, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

// A palette that's readable on light + dark; recycled for large casts.
const CAST_COLORS = [
  '#c2410c', '#0e7490', '#4d7c0f', '#7c3aed', '#be185d',
  '#0369a1', '#a16207', '#15803d', '#b91c1c', '#6d28d9',
];

const SCENE_RE = /^\s*(?:(?:INT\.|EXT\.|EST\.|INT\/EXT|I\/E|INT\.\s*\/\s*EXT\.).+|\.[^.].+)$/i;

/** Walk the fountain source once and return an ordered event stream. */
function parseFountain(fountain) {
  const out = [];
  let currentChar = null;
  const lines = (fountain || '').split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const stripped = raw.trim();
    if (!stripped) { currentChar = null; continue; }
    // Skip title-page metadata (`Title:`, `Author:`, …) unless it's a slug.
    if (/^[A-Za-z ]{2,20}:\s*/.test(stripped) &&
        !/^(INT\.|EXT\.)/i.test(stripped)) continue;
    // Chapter marker → announce as a heading.
    if (stripped.startsWith('= ') && stripped.endsWith(' =')) {
      out.push({ type: 'heading', text: stripped.replace(/^= | =$/g, '').trim(), line: i + 1 });
      currentChar = null; continue;
    }
    if (stripped === '===') continue;
    if (SCENE_RE.test(stripped)) {
      const heading = stripped.startsWith('.') ? stripped.replace(/^\./, '').trim() : stripped;
      out.push({ type: 'scene', text: heading, line: i + 1 });
      currentChar = null; continue;
    }
    // Parentheticals — attach to preceding character but don't speak.
    if (stripped.startsWith('(') && stripped.endsWith(')') && currentChar) {
      out.push({ type: 'parenthetical', character: currentChar, text: stripped, line: i + 1 });
      continue;
    }
    // ALL CAPS short line = character cue.
    if (stripped === stripped.toUpperCase() && stripped.length < 40 &&
        /[A-Z]/.test(stripped)) {
      // Collapse Fountain suffixes like `MARCUS (CONT'D)`, `ELENA (V.O.)`,
      // `NARRATOR (O.S.)` back to the base name — they're the same cast.
      currentChar = stripped.replace(
        /\s*\((?:CONT'?D|V\.?O\.?|O\.?S\.?|OFF|PRE\-?LAP)\)\s*$/i, '',
      ).trim();
      continue;
    }
    if (currentChar) {
      out.push({ type: 'dialogue', character: currentChar, text: stripped, line: i + 1 });
      continue;
    }
    out.push({ type: 'action', text: stripped, line: i + 1 });
  }
  return out;
}

/** Build a map of character → { color, voiceName }. Colors recycle. */
function buildCastMap(events, existing = {}) {
  const names = Array.from(new Set(
    events.filter((e) => e.character).map((e) => e.character),
  ));
  const next = { ...existing };
  names.forEach((n, i) => {
    if (!next[n]) {
      next[n] = { color: CAST_COLORS[i % CAST_COLORS.length], voiceURI: null };
    }
  });
  return next;
}

export default function ScriptReadThrough({ open, onClose, script }) {
  const events = useMemo(() => parseFountain(script?.fountain), [script?.fountain]);
  const [cast, setCast]         = useState({});
  const [voices, setVoices]     = useState([]);
  const [rate,  setRate]        = useState(1.0);
  const [idx,   setIdx]         = useState(0);
  const [playing, setPlaying]   = useState(false);
  const [ready, setReady]       = useState(false);
  const utterRef                = useRef(null);
  const bodyRef                 = useRef(null);

  // Hydrate voices when the dialog opens (browsers load them async).
  useEffect(() => {
    if (!open) return;
    const load = () => {
      const list = window.speechSynthesis?.getVoices?.() || [];
      setVoices(list);
      setReady(true);
    };
    load();
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = load;
    }
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = null;
    };
  }, [open]);

  // Rebuild cast whenever the script changes.
  useEffect(() => {
    setCast((prev) => buildCastMap(events, prev));
    setIdx(0);
  }, [script?.id, events]);

  // Auto-assign a distinct voice per character when voices arrive.
  useEffect(() => {
    if (!voices.length) return;
    setCast((prev) => {
      const next = { ...prev };
      let vi = 0;
      Object.keys(next).forEach((name) => {
        if (!next[name].voiceURI) {
          const v = voices[vi % voices.length];
          next[name] = { ...next[name], voiceURI: v.voiceURI };
          vi += 1;
        }
      });
      return next;
    });
  }, [voices]);

  // Stop everything on close/unmount.
  useEffect(() => {
    if (!open && window.speechSynthesis) window.speechSynthesis.cancel();
    return () => { if (window.speechSynthesis) window.speechSynthesis.cancel(); };
  }, [open]);

  // Scroll the active event into view.
  useEffect(() => {
    if (!bodyRef.current) return;
    const el = bodyRef.current.querySelector(`[data-evt="${idx}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [idx]);

  const speakEvent = (evt, onEnd) => {
    if (!evt || !window.speechSynthesis) { onEnd?.(); return; }
    // Skip parentheticals — we display them but don't speak them.
    if (evt.type === 'parenthetical') { onEnd?.(); return; }
    const u = new SpeechSynthesisUtterance(evt.text);
    u.rate = rate;
    // Character voice — narrator for actions/scenes/headings.
    if (evt.character && cast[evt.character]?.voiceURI) {
      const v = voices.find((x) => x.voiceURI === cast[evt.character].voiceURI);
      if (v) u.voice = v;
    } else if (voices[0]) {
      u.voice = voices[0];   // narrator = first voice by default
      u.pitch = 0.9;
    }
    u.onend = () => onEnd?.();
    u.onerror = () => onEnd?.();
    utterRef.current = u;
    window.speechSynthesis.speak(u);
  };

  const step = (from) => {
    if (from >= events.length) { setPlaying(false); return; }
    setIdx(from);
    speakEvent(events[from], () => {
      // Auto-advance only if still playing (user might have paused).
      setPlaying((p) => {
        if (p) setTimeout(() => step(from + 1), 60);
        return p;
      });
    });
  };

  const play = () => {
    if (!events.length) return;
    setPlaying(true);
    // If we were mid-line and paused via `pause()`, resume; else start fresh.
    if (window.speechSynthesis?.paused) {
      window.speechSynthesis.resume();
      return;
    }
    step(idx);
  };
  const pause = () => {
    setPlaying(false);
    if (window.speechSynthesis?.speaking) window.speechSynthesis.pause();
  };
  const stop = () => {
    setPlaying(false);
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIdx(0);
  };
  const stepFwd = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIdx((i) => Math.min(events.length - 1, i + 1));
  };
  const stepBack = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIdx((i) => Math.max(0, i - 1));
  };

  const assignVoice = (name, voiceURI) => {
    setCast((prev) => ({ ...prev, [name]: { ...prev[name], voiceURI } }));
  };

  const characters = Object.keys(cast);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="flex max-h-[85vh] max-w-4xl flex-col overflow-hidden"
        data-testid="script-readthrough-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Volume2 className="h-4 w-4 text-primary" />
            Read-through · {script?.title || '(untitled)'}
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Assign a voice + colour to each character, then press play to
            hear the script performed. Great for spotting stilted dialogue
            before your first read-through. Uses your browser's built-in
            voices — no data leaves this tab.
          </DialogDescription>
        </DialogHeader>

        {!events.length && (
          <div className="grid place-items-center py-8 text-[11.5px] text-muted-foreground">
            {ready ? 'No spoken content parsed from this script.' : <Loader2 className="h-4 w-4 animate-spin" />}
          </div>
        )}

        {events.length > 0 && (
          <div className="grid flex-1 grid-cols-12 gap-2 overflow-hidden">
            {/* Cast panel */}
            <div className="col-span-4 flex flex-col overflow-hidden rounded-md border border-panel-border bg-card">
              <div className="flex items-center gap-1 border-b border-panel-border bg-panel-header px-2 py-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                <Users className="h-3 w-3" /> Cast ({characters.length})
              </div>
              <div className="flex-1 overflow-auto p-2" data-testid="readthrough-cast">
                {characters.length === 0 && (
                  <div className="text-[10.5px] italic text-muted-foreground">
                    No named characters detected.
                  </div>
                )}
                <ul className="space-y-1.5">
                  {characters.map((name) => (
                    <li key={name} className="rounded-sm border border-panel-border p-1.5" data-testid={`readthrough-cast-${name}`}>
                      <div className="flex items-center gap-1.5">
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ background: cast[name].color }}
                        />
                        <span className="text-[11.5px] font-semibold" style={{ color: cast[name].color }}>{name}</span>
                      </div>
                      <select
                        value={cast[name].voiceURI || ''}
                        onChange={(e) => assignVoice(name, e.target.value)}
                        data-testid={`readthrough-voice-${name}`}
                        className="mt-1 w-full rounded-sm border border-panel-border bg-background px-1 py-0.5 text-[10.5px]"
                        disabled={!voices.length}
                      >
                        <option value="">(silent)</option>
                        {voices.map((v) => (
                          <option key={v.voiceURI} value={v.voiceURI}>
                            {v.name}{v.lang ? ` · ${v.lang}` : ''}
                          </option>
                        ))}
                      </select>
                    </li>
                  ))}
                </ul>
                {!voices.length && (
                  <div className="mt-2 rounded-sm border border-panel-border bg-secondary p-1.5 text-[10px] text-muted-foreground">
                    Your browser reports no TTS voices. Silent walkthrough
                    still works — the highlighted line advances so you can
                    read along.
                  </div>
                )}
              </div>
            </div>

            {/* Transcript panel */}
            <div className="col-span-8 flex flex-col overflow-hidden rounded-md border border-panel-border bg-card">
              <div className="flex flex-wrap items-center gap-1 border-b border-panel-border bg-panel-header px-2 py-1">
                <Button size="sm" onClick={stepBack} className="h-6 px-1.5" variant="ghost" data-testid="readthrough-back" title="Previous line">
                  <SkipBack className="h-3 w-3" />
                </Button>
                {playing ? (
                  <Button size="sm" onClick={pause} className="h-6 gap-1 px-2 text-[10.5px]" data-testid="readthrough-pause">
                    <Pause className="h-3 w-3" /> Pause
                  </Button>
                ) : (
                  <Button size="sm" onClick={play} className="h-6 gap-1 px-2 text-[10.5px]" data-testid="readthrough-play">
                    <Play className="h-3 w-3" /> Play
                  </Button>
                )}
                <Button size="sm" onClick={stop} className="h-6 px-1.5" variant="ghost" data-testid="readthrough-stop" title="Stop">
                  <Square className="h-3 w-3" />
                </Button>
                <Button size="sm" onClick={stepFwd} className="h-6 px-1.5" variant="ghost" data-testid="readthrough-fwd" title="Next line">
                  <SkipForward className="h-3 w-3" />
                </Button>
                <span className="mx-2 text-[10.5px] text-muted-foreground">
                  {idx + 1} / {events.length}
                </span>
                <label className="ml-auto flex items-center gap-1 text-[10.5px] text-muted-foreground">
                  Speed
                  <input
                    type="range" min="0.6" max="1.6" step="0.05"
                    value={rate}
                    onChange={(e) => setRate(parseFloat(e.target.value))}
                    data-testid="readthrough-speed"
                    className="h-1 w-24 accent-primary"
                  />
                  {rate.toFixed(2)}×
                </label>
              </div>
              <div
                ref={bodyRef}
                className="flex-1 overflow-auto p-3"
                data-testid="readthrough-transcript"
              >
                {events.map((e, i) => {
                  const isActive = i === idx;
                  const color = e.character && cast[e.character]?.color;
                  return (
                    <div
                      key={i}
                      data-evt={i}
                      data-testid={`readthrough-line-${i}`}
                      onClick={() => { stop(); setIdx(i); }}
                      className={
                        'cursor-pointer rounded-sm px-1.5 py-0.5 text-[12px] transition-colors '
                        + (isActive ? 'bg-primary/15 ring-1 ring-primary/40' : 'hover:bg-secondary')
                      }
                    >
                      {e.type === 'scene' && (
                        <div className="mt-2 text-[11px] font-bold uppercase text-muted-foreground">{e.text}</div>
                      )}
                      {e.type === 'heading' && (
                        <div className="mt-2 border-b border-panel-border pb-1 text-[12px] font-bold">{e.text}</div>
                      )}
                      {e.type === 'action' && (
                        <div className="italic text-[11.5px] text-foreground/80">{e.text}</div>
                      )}
                      {e.type === 'dialogue' && (
                        <div>
                          <div className="text-[10.5px] font-semibold" style={color ? { color } : undefined}>
                            {e.character}
                          </div>
                          <div className="text-[12px]">{e.text}</div>
                        </div>
                      )}
                      {e.type === 'parenthetical' && (
                        <div className="pl-4 text-[10.5px] italic text-muted-foreground">{e.text}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => { stop(); onClose(); }}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
